// MonthlyGrid — Hierarchical spend plan monthly worksheet

// Renders a work-plan-linked spend plan as a hierarchical grid:
//   Component → Activity → Contract (optional)
// Category is shown as metadata (sub-text) on each activity row, along with
// allocation amount and date range. Planning happens at component/activity level.

// react
import { FC, useCallback, useMemo, useState } from 'react';

// mui
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import {
	Add as IconAdd,
	Close as IconClose,
	ExpandMore as IconExpandMore,
	ChevronRight as IconChevronRight,
	Save as IconSave,
} from '@mui/icons-material';

// components
import { TableSheet } from '../../../ui/TableSheet/TableSheet';

// hooks
import { useMonthlyAllocations } from '../../../../hooks/useMonthlyAllocations';

// utils
import { formatCurrency } from '../../../../utils/currency';

// types
import { MonthlyAllocationGridRow } from '../../../../services/api';
import { Account } from '../../../../@types';

// theme
import { theme } from '../../../../theme';

// Helpers

/** Generate an array of first-of-month date strings between two dates. */
const getMonthColumns = (dateStart: string, dateEnd: string): string[] => {
	const months: string[] = [];
	const start = new Date(dateStart);
	const end = new Date(dateEnd);
	// Use UTC methods to avoid timezone offset shifting dates back one day
	const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
	const lastMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

	while (cursor <= lastMonth) {
		const y = cursor.getUTCFullYear();
		const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
		months.push(`${y}-${m}-01`);
		cursor.setUTCMonth(cursor.getUTCMonth() + 1);
	}
	return months;
};

/** Format '2026-03-01' → 'Mar 2026' */
const formatMonthLabel = (monthKey: string): string => {
	const d = new Date(monthKey);
	return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** Get quarter index (0-based) for a month key */
const getQuarter = (monthKey: string): number => {
	const m = new Date(monthKey).getUTCMonth();
	return Math.floor(m / 3);
};

/** Quarter-alternating subtle background for column consistency */
const quarterBg = (monthKey: string, base?: string): string => {
	const q = getQuarter(monthKey);
	if (base) return base; // explicit bg takes precedence
	return q % 2 === 0 ? 'rgba(37, 99, 235, 0.04)' : 'rgba(0, 0, 0, 0.02)';
};

/** Indent padding for hierarchy depth */
const indent = (depth: number) => `${12 + depth * 24}px`;

/** Build a subtitle string for activity metadata (category, allocation, date range).
 *  Rendered via CSS ::after on the name cell. */
const activitySubtext = (
	categoryName: string | null,
	amount: number,
	currency: string | null,
	dateStart: string | null,
	dateEnd: string | null,
	// When the activity is budgeted in a different currency than the spend
	// plan's worksheet (e.g. a USD activity planned from a PHP account), the
	// budget figure is restated into the plan currency so the reader can bridge
	// the two without doing the math. Omitted when no rate is available.
	planCurrency?: string | null,
	localPerReportingRate?: number | null
): string => {
	const parts: string[] = [];
	if (categoryName) parts.push(categoryName);
	if (amount > 0) {
		const cur = currency || 'USD';
		let budget = `${cur} ${amount.toLocaleString()}`;
		if (
			planCurrency &&
			cur !== planCurrency &&
			localPerReportingRate != null &&
			localPerReportingRate > 0
		) {
			const inPlan = amount * localPerReportingRate;
			budget += ` (≈ ${formatCurrency({ value: inPlan, currency: planCurrency })})`;
		}
		parts.push(budget);
	}
	if (dateStart || dateEnd) {
		const fmt = (d: string) =>
			new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
		if (dateStart && dateEnd) {
			parts.push(`${fmt(dateStart)} – ${fmt(dateEnd)}`);
		} else if (dateStart) {
			parts.push(`From ${fmt(dateStart)}`);
		} else if (dateEnd) {
			parts.push(`Until ${fmt(dateEnd)}`);
		}
	}
	return parts.join(' · ');
};

/** Escape a string for use in CSS content: "..." */
const escapeCssContent = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/** Hierarchy indicator colors */
const HIERARCHY_COLORS = {
	component: '#2563eb', // blue-600
	category: '#7c3aed', // violet-600
	activity: '#059669', // emerald-600
	contract: '#d97706', // amber-600
	account: '#6366f1', // indigo-500
};

/** Material Design icon SVGs for hierarchy levels (matching app icon conventions:
 *  Component → Extension, Category → Category, Activity → Work, Contract → Description) */
const HIERARCHY_ICON_SVGS: Record<string, string> = {
	component: encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="black" d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z"/></svg>'
	),
	category: encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="black" d="M12 2l-5.5 9h11z"/><circle fill="black" cx="17.5" cy="17.5" r="2.5"/><path fill="black" d="M3 13.5h8v8H3z"/></svg>'
	),
	activity: encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="black" d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z"/></svg>'
	),
	contract: encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="black" d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>'
	),
	account: encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="black" d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>'
	),
};

/** CSS ::before icon indicator for hierarchy levels using SVG masks */
const hierarchyIcon = (color: string, level: keyof typeof HIERARCHY_ICON_SVGS, size = 16) => {
	const maskUrl = `url("data:image/svg+xml,${HIERARCHY_ICON_SVGS[level]}")`;
	return {
		// Ensure the icon (::before) and text (Typography) stay on the same line
		// while still allowing ::after subtitle to wrap to a new row.
		'& > *': { flex: '1 1 0px', minWidth: 0 },
		'&::before': {
			content: '""',
			display: 'inline-block',
			width: size,
			height: size,
			flex: '0 0 auto',
			marginRight: '8px',
			backgroundColor: color,
			maskImage: maskUrl,
			maskSize: 'contain',
			maskRepeat: 'no-repeat',
			WebkitMaskImage: maskUrl,
			WebkitMaskSize: 'contain',
			WebkitMaskRepeat: 'no-repeat',
		},
	};
};

// Cell widths
const CELL_WIDTH_NAME = 380;
const CELL_WIDTH_MONTH = 130;
const CELL_WIDTH_TOTAL = 150;

// Types

interface HierarchyNode {
	componentId: number | null;
	componentName: string | null;
	activityId: number;
	activityName: string;
	categoryName: string | null;
	activityAmount: number;
	activityCurrency: string | null;
	activityDateStart: string | null;
	activityDateEnd: string | null;
	rows: MonthlyAllocationGridRow[]; // activity-level + contract-level rows
}

// Props

export interface MonthlyGridProps {
	spendPlanId: number;
	spendPlanStatus?: string;
	/** Project accounts available for splitting allocations across accounts */
	accounts?: Account[];
	/** Called after a successful save so the parent can refresh totals */
	onSaveComplete?: () => void;
	/** Optional extra content to render in the toolbar row (left side) */
	toolbarLeft?: React.ReactNode;
	/** Currency the worksheet cells are denominated in (the plan's account currency). */
	planCurrency?: string | null;
	/** Project reporting currency, used for the worksheet-total equivalent. */
	reportingCurrency?: string | null;
	/**
	 * Plan-currency units per 1 reporting-currency unit (the work plan's budget
	 * rate). Drives the dual-currency activity budget and the reporting-currency
	 * worksheet total. Omitted/null when no local budget exists to derive it.
	 */
	localPerReportingRate?: number | null;
}

// Component

export const MonthlyGrid: FC<MonthlyGridProps> = ({
	spendPlanId,
	spendPlanStatus,
	accounts = [],
	onSaveComplete,
	toolbarLeft,
	planCurrency,
	reportingCurrency,
	localPerReportingRate,
}) => {
	const isEditable = spendPlanStatus === 'DRAFT' || spendPlanStatus === 'ACTIVE';

	const { data, error, loading, saving, updateCell, saveAll, refetch, deactivateAccountSplit } =
		useMonthlyAllocations({
			spendPlanId,
			onSaveComplete,
		});

	// =========================================================================
	// Account split state
	// =========================================================================

	/** Tracks which row the "add account" menu is anchored to */
	const [splitMenuAnchor, setSplitMenuAnchor] = useState<HTMLElement | null>(null);
	const [splitTarget, setSplitTarget] = useState<{
		activityId: number;
		contractId: number | null;
	} | null>(null);

	const handleOpenSplitMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>, activityId: number, contractId: number | null) => {
			setSplitMenuAnchor(event.currentTarget);
			setSplitTarget({ activityId, contractId });
		},
		[]
	);

	const handleCloseSplitMenu = useCallback(() => {
		setSplitMenuAnchor(null);
		setSplitTarget(null);
	}, []);

	// =========================================================================
	// Contract expansion state (per-activity toggle)
	// Default: collapsed (activity-level allocation). Expanded = contract detail.
	// =========================================================================

	/** Set of activityIds that have contract rows expanded */
	const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());

	/** Activities the user explicitly collapsed — never auto-expand these again */
	const [, setUserCollapsed] = useState<Set<number>>(new Set());

	/** Track whether initial auto-expand has already run */
	const [initialExpandDone, setInitialExpandDone] = useState(false);

	/** Auto-detect expansion on INITIAL load only — respect user's manual collapse */
	useMemo(() => {
		if (!data || initialExpandDone) return;
		const shouldExpand = new Set<number>();
		for (const row of data.rows) {
			if (row.contractId != null && row.total > 0) {
				shouldExpand.add(row.activityId);
			}
		}
		if (shouldExpand.size > 0) {
			setExpandedActivities(shouldExpand);
		}
		setInitialExpandDone(true);
	}, [data, initialExpandDone]);

	const toggleContractExpansion = useCallback((activityId: number) => {
		setExpandedActivities((prev) => {
			const next = new Set(prev);
			if (next.has(activityId)) {
				next.delete(activityId);
				// Remember this was user-collapsed so auto-expand won't override
				setUserCollapsed((uc) => {
					const n = new Set(uc);
					n.add(activityId);
					return n;
				});
			} else {
				next.add(activityId);
				// User re-expanded — remove from collapsed set
				setUserCollapsed((uc) => {
					const n = new Set(uc);
					n.delete(activityId);
					return n;
				});
			}
			return next;
		});
	}, []);

	// =========================================================================
	// Month columns
	// =========================================================================

	const monthKeys = useMemo(() => {
		if (!data) return [];
		return getMonthColumns(data.dateStart, data.dateEnd);
	}, [data]);

	// =========================================================================
	// Columns
	// =========================================================================

	const columns: GridColDef[] = useMemo(() => {
		const cols: GridColDef[] = [
			{
				field: 'name',
				headerName: 'Description',
				// Grow to fill available width so the grid uses the full container
				// instead of leaving whitespace, while never shrinking below the
				// readable floor. This also gives long descriptions room to wrap
				// rather than overflow when the user increases their font size.
				minWidth: CELL_WIDTH_NAME,
				flex: 1,
			},
		];

		for (const mk of monthKeys) {
			cols.push({
				field: mk,
				headerName: formatMonthLabel(mk),
				minWidth: CELL_WIDTH_MONTH,
			});
		}

		cols.push({
			field: 'total',
			headerName: 'Total',
			minWidth: CELL_WIDTH_TOTAL,
		});

		return cols;
	}, [monthKeys]);

	// =========================================================================
	// Hierarchical grouping
	// =========================================================================

	const hierarchy = useMemo(() => {
		if (!data) return [];

		// Group rows by activity, merging activity-level and contract-level rows
		const activityMap = new Map<number, HierarchyNode>();
		for (const row of data.rows) {
			const existing = activityMap.get(row.activityId);
			if (existing) {
				existing.rows.push(row);
			} else {
				activityMap.set(row.activityId, {
					componentId: row.componentId,
					componentName: row.componentName,
					activityId: row.activityId,
					activityName: row.activityName,
					categoryName: row.categoryName,
					activityAmount: row.activityAmount ?? 0,
					activityCurrency: row.activityCurrency ?? null,
					activityDateStart: row.activityDateStart ?? null,
					activityDateEnd: row.activityDateEnd ?? null,
					rows: [row],
				});
			}
		}

		return Array.from(activityMap.values());
	}, [data]);

	// =========================================================================
	// BoxProps styling helpers
	// =========================================================================

	const fieldsSticky = useMemo(() => ({ left: ['name'], right: ['total'] }), []);

	const fieldsEditable = useMemo(() => (isEditable ? monthKeys : []), [isEditable, monthKeys]);

	const makeHeaderBoxProps = useCallback(
		(monthKeys: string[]) => {
			const bb = `1px solid ${theme.palette.brand.blue[300]}`;
			const props: Record<string, { sx: Record<string, unknown> }> = {
				name: {
					sx: {
						backgroundColor: theme.palette.brand.blue[200],
						color: theme.palette.brand.blue[800],
						fontWeight: 700,
						justifyContent: 'flex-start',
						borderLeft: `3px solid ${theme.palette.brand.blue[600]}`,
						borderBottom: bb,
					},
				},
				total: {
					sx: {
						backgroundColor: theme.palette.brand.blue[200],
						color: theme.palette.brand.blue[800],
						fontWeight: 700,
						justifyContent: 'flex-end',
						borderBottom: bb,
					},
				},
			};
			for (const mk of monthKeys) {
				const q = getQuarter(mk);
				props[mk] = {
					sx: {
						backgroundColor:
							q % 2 === 0 ? theme.palette.brand.blue[100] : theme.palette.brand.grey[100],
						color: theme.palette.brand.blue[800],
						fontWeight: 600,
						justifyContent: 'center',
						borderBottom: bb,
					},
				};
			}
			return props;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	/** Build month-column BoxProps for group rows */
	const monthBoxProps = useCallback(
		(bgColor: string) => {
			const props: Record<string, { sx: Record<string, unknown> }> = {};
			for (const mk of monthKeys) {
				props[mk] = {
					sx: {
						backgroundColor: bgColor,
						color: theme.palette.brand.grey[500],
						fontWeight: 500,
						justifyContent: 'flex-end',
						borderBottom: `1px solid ${theme.palette.brand.grey[200]}`,
					},
				};
			}
			return props;
		},
		[monthKeys]
	);

	const groupRowBoxProps = useCallback(
		(depth: number, level: 'component' | 'activity') => {
			const isComponent = level === 'component';
			const indicatorColor = isComponent ? HIERARCHY_COLORS.component : HIERARCHY_COLORS.activity;

			// Component: solid blue, Activity: subtle green tint
			const bgColor = isComponent
				? (theme.palette.brand.blue[200] as string)
				: 'rgba(5, 150, 105, 0.08)'; // emerald-600 at 8% opacity
			const borderColor = isComponent ? theme.palette.brand.blue[300] : 'rgba(5, 150, 105, 0.18)';
			const textColor = isComponent ? theme.palette.brand.blue[800] : theme.palette.brand.grey[800];

			return {
				name: {
					sx: {
						backgroundColor: bgColor,
						fontWeight: isComponent ? 700 : 600,
						color: textColor,
						justifyContent: 'flex-start',
						alignItems: 'center',
						pl: indent(depth),
						borderLeft: `3px solid ${indicatorColor}`,
						borderBottom: `1px solid ${borderColor}`,
						...hierarchyIcon(indicatorColor, level, isComponent ? 18 : 16),
					},
				},
				total: {
					sx: {
						backgroundColor: bgColor,
						fontWeight: isComponent ? 700 : 600,
						color: textColor,
						justifyContent: 'flex-end',
						borderBottom: `1px solid ${borderColor}`,
					},
				},
				...monthBoxProps(bgColor),
			};
		},
		[monthBoxProps]
	);

	const dataRowBoxProps = useCallback(
		(depth: number, level: 'activity' | 'contract') => {
			const indicatorColor =
				level === 'activity' ? HIERARCHY_COLORS.activity : HIERARCHY_COLORS.contract;
			const nameBg =
				level === 'contract'
					? 'rgba(217, 119, 6, 0.03)' // amber hint for contracts
					: theme.palette.common.white;
			const dataMonthProps: Record<string, { sx: Record<string, unknown> }> = {};
			for (const mk of monthKeys) {
				dataMonthProps[mk] = {
					sx: {
						backgroundColor: quarterBg(mk),
						justifyContent: 'flex-end',
					},
				};
			}
			return {
				name: {
					sx: {
						backgroundColor: nameBg,
						justifyContent: 'flex-start',
						alignItems: 'center',
						fontWeight: 500,
						pl: indent(depth),
						borderLeft: `3px solid ${indicatorColor}`,
						borderBottom: `1px solid ${theme.palette.brand.grey[100]}`,
						...hierarchyIcon(indicatorColor, level, 14),
					},
				},
				total: {
					sx: {
						backgroundColor: theme.palette.brand.blue[100],
						fontWeight: 600,
						justifyContent: 'flex-end',
						borderBottom: `1px solid ${theme.palette.brand.grey[100]}`,
					},
				},
				...dataMonthProps,
			};
		},
		[monthKeys]
	);

	const totalBoxProps = useMemo(() => {
		const props: Record<string, { sx: Record<string, unknown> }> = {
			name: {
				sx: {
					backgroundColor: theme.palette.brand.blue[200],
					color: theme.palette.brand.blue[800],
					fontWeight: 700,
					justifyContent: 'flex-start',
					borderLeft: `3px solid ${theme.palette.brand.blue[600]}`,
					borderTop: `1px solid ${theme.palette.brand.blue[300]}`,
				},
			},
			total: {
				sx: {
					backgroundColor: theme.palette.brand.blue[300],
					color: theme.palette.brand.blue[800],
					fontWeight: 700,
					justifyContent: 'flex-end',
					borderTop: `1px solid ${theme.palette.brand.blue[300]}`,
				},
			},
		};
		for (const mk of monthKeys) {
			props[mk] = {
				sx: {
					backgroundColor: theme.palette.brand.blue[200],
					color: theme.palette.brand.blue[800],
					fontWeight: 700,
					justifyContent: 'flex-end',
					borderTop: `1px solid ${theme.palette.brand.blue[300]}`,
				},
			};
		}
		return props;
	}, [monthKeys]);

	/** BoxProps for account sub-rows (shown when an activity/contract is split across accounts) */
	const accountRowBoxProps = useCallback(
		(depth: number) => {
			const accountColor = HIERARCHY_COLORS.account;
			const dataMonthProps: Record<string, { sx: Record<string, unknown> }> = {};
			for (const mk of monthKeys) {
				dataMonthProps[mk] = {
					sx: {
						backgroundColor: quarterBg(mk),
						justifyContent: 'flex-end',
					},
				};
			}
			return {
				name: {
					sx: {
						backgroundColor: 'rgba(99, 102, 241, 0.04)', // indigo-500 subtle tint
						justifyContent: 'flex-start',
						alignItems: 'center',
						fontWeight: 500,
						fontSize: '1.25rem',
						pl: indent(depth),
						borderLeft: `3px solid ${accountColor}`,
						borderBottom: `1px solid ${theme.palette.brand.grey[100]}`,
						color: theme.palette.brand.grey[700],
						...hierarchyIcon(accountColor, 'account', 20),
					},
				},
				total: {
					sx: {
						backgroundColor: 'rgba(99, 102, 241, 0.06)',
						fontWeight: 600,
						fontSize: '1.25rem',
						justifyContent: 'flex-end',
						borderBottom: `1px solid ${theme.palette.brand.grey[100]}`,
					},
				},
				...dataMonthProps,
			};
		},
		[monthKeys]
	);

	// =========================================================================
	// Rows — build flat rows with hierarchical visual structure
	// Component → Activity → Contract  (category is metadata on activity)
	// Account splits appear as sub-rows only when a row is split across accounts.
	// =========================================================================

	/** Deactivate an account split — soft-deletes all monthly rows for that account combo */
	const handleDeactivateAccountSplit = useCallback(
		async (activityId: number, contractId: number | null, accountId: number) => {
			if (!window.confirm('Deactivate this account split? Any amounts entered will be hidden.')) {
				return;
			}
			await deactivateAccountSplit(activityId, contractId, accountId);
		},
		[deactivateAccountSplit]
	);

	const rows: GridValidRowModel[] = useMemo(() => {
		if (!data) return [];

		const result: GridValidRowModel[] = [];
		const headerBP = makeHeaderBoxProps(monthKeys);

		// --- Header row ---
		const headerRow: Record<string, unknown> = {
			id: 'header',
			name: 'Description',
			total: 'Total',
			BoxProps: headerBP,
			fieldsSticky,
		};
		for (const mk of monthKeys) {
			headerRow[mk] = formatMonthLabel(mk);
		}
		result.push(headerRow);

		// --- Group by component ---
		const compMap = new Map<string, { name: string; nodes: HierarchyNode[] }>();
		for (const node of hierarchy) {
			const compKey = node.componentId != null ? String(node.componentId) : 'unassigned';
			const compName = node.componentName ?? 'Unassigned Component';
			if (!compMap.has(compKey)) {
				compMap.set(compKey, { name: compName, nodes: [] });
			}
			const entry = compMap.get(compKey);
			if (entry) entry.nodes.push(node);
		}

		for (const [compKey, compGroup] of compMap) {
			let compTotal = 0;
			const compMonths: Record<string, number> = {};

			// Component header row (depth 0)
			const compRow: Record<string, unknown> = {
				id: `comp-${compKey}`,
				name: compGroup.name,
				total: 0, // placeholder — computed below
				BoxProps: groupRowBoxProps(0, 'component'),
				fieldsSticky,
			};
			for (const mk of monthKeys) {
				compRow[mk] = '—';
			}
			const compRowIdx = result.length;
			result.push(compRow);

			// Activity nodes (depth 1)
			for (const node of compGroup.nodes) {
				const activityLevelRows = node.rows.filter((r) => r.contractId == null);
				const contractRows = node.rows.filter((r) => r.contractId != null);
				const hasContracts = contractRows.length > 0;
				const actDepth = 1;

				// Build activity subtitle (category · allocation · date range)
				const subtitle = activitySubtext(
					node.categoryName,
					node.activityAmount,
					node.activityCurrency,
					node.activityDateStart,
					node.activityDateEnd,
					planCurrency,
					localPerReportingRate
				);
				const subtitleCss = subtitle
					? {
							flexWrap: 'wrap' as const,
							alignItems: 'flex-start' as const,
							py: '6px',
							'&::after': {
								content: `"${escapeCssContent(subtitle)}"`,
								display: 'block',
								width: '100%',
								fontSize: '1.25rem',
								color: theme.palette.brand.grey[500],
								fontWeight: 400,
								lineHeight: 1.3,
								pl: '30px', // align with text after icon (20px icon + 8px margin + 2px)
								mt: '1px',
							},
						}
					: {};

				// Helper: render a set of rows for a (activityId, contractId) combo
				// If multiple accounts → separate sub-rows per account
				// If single or no account → one row with account label chip
				const canSplit = isEditable && accounts.length > 0;

				/** Build a small account label chip for a row's account attribution */
				const accountLabelChip = (
					accountName: string | null,
					accountId: number | null
				): React.ReactNode => {
					if (accountId == null && !accountName) return null;
					const label = accountName || `Account #${accountId}`;
					return (
						<Chip
							size="small"
							label={label}
							variant="outlined"
							sx={{
								ml: 1,
								height: 28,
								fontSize: '1.25rem',
								fontWeight: 500,
								borderColor: HIERARCHY_COLORS.account + '55',
								color: HIERARCHY_COLORS.account,
								backgroundColor: 'rgba(99, 102, 241, 0.06)',
							}}
						/>
					);
				};

				// renderDataRows captures per-iteration `compTotal` / `compMonths`. The
				// closure is invoked synchronously during the same iteration, so the
				// no-loop-func rule's concern (stale captures) doesn't apply here.
				// eslint-disable-next-line no-loop-func
				const renderDataRows = (
					rows: MonthlyAllocationGridRow[],
					depth: number,
					level: 'activity' | 'contract',
					nameFn: (row: MonthlyAllocationGridRow) => string,
					baseBPFn: () => Record<string, { sx: Record<string, unknown> }>,
					extraNameSx?: Record<string, unknown>
				) => {
					// Only count rows with actual account assignment as "split"
					const accountRows = rows.filter((r) => r.accountId != null);
					const isSplit = accountRows.length > 1 || (accountRows.length === 1 && rows.length > 1);

					for (const dRow of rows) {
						// Skip null-account rows when real account rows exist (the values
						// are aggregated into the group header already)
						if (dRow.accountId == null && accountRows.length > 0) continue;

						const idSuffix =
							dRow.accountId != null
								? `${dRow.activityId}-${dRow.contractId}-acct${dRow.accountId}`
								: `${dRow.activityId}-${dRow.contractId}`;
						const rowId = level === 'contract' ? `contract-${idSuffix}` : `act-${idSuffix}`;

						let bp: Record<string, { sx?: Record<string, unknown> } & Record<string, unknown>>;
						let displayName: string;

						if (isSplit && dRow.accountId != null) {
							// Account sub-row style
							bp = accountRowBoxProps(depth);
							displayName = dRow.accountName || `Account #${dRow.accountId}`;
						} else {
							bp = baseBPFn();
							displayName = nameFn(dRow);
						}

						// Apply extra name sx (e.g. subtitle CSS)
						if (extraNameSx) {
							bp = {
								...bp,
								name: {
									...bp.name,
									sx: {
										...bp.name.sx,
										...extraNameSx,
									},
								},
							};
						}

						const rowData: Record<string, unknown> = {
							id: rowId,
							name: displayName,
							total: dRow.total,
							activityId: dRow.activityId,
							contractId: dRow.contractId,
							accountId: dRow.accountId,
							BoxProps: bp,
							fieldsEditable,
							fieldsSticky,
						};
						for (const mk of monthKeys) {
							const amt = dRow.months[mk] ?? 0;
							rowData[mk] = amt;
							compTotal += amt;
							compMonths[mk] = (compMonths[mk] || 0) + amt;
						}

						// For account sub-rows in an editable split, add a deactivate (×) button
						// (no account chip here — the row name IS the account name)
						if (isSplit && dRow.accountId != null && isEditable) {
							rowData.isRemovableSplit = true;
							rowData.fieldsSuffix = {
								name: (
									<Tooltip title="Deactivate this account split" enterDelay={300}>
										<IconButton
											size="small"
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation();
												handleDeactivateAccountSplit(
													dRow.activityId,
													dRow.contractId,
													dRow.accountId ?? 0
												);
											}}
											sx={{
												ml: 0.5,
												p: 0.25,
												opacity: 0.4,
												transition: 'opacity 0.15s',
												'&:hover': {
													opacity: 1,
													backgroundColor: theme.palette.error.light + '22',
												},
												color: theme.palette.error.main,
											}}>
											<IconClose sx={{ fontSize: 20 }} />
										</IconButton>
									</Tooltip>
								),
							};
						} else if (!isSplit && dRow.accountId != null) {
							// Single account — always show which account the amounts are on
							const existingSuffix =
								(rowData.fieldsSuffix as Record<string, React.ReactNode>) || {};
							rowData.fieldsSuffix = {
								...existingSuffix,
								name: (
									<>
										{existingSuffix.name}
										{accountLabelChip(dRow.accountName, dRow.accountId)}
									</>
								),
							};
						}

						result.push(rowData);
					}
				};

				/**
				 * Attach an "add account" trigger to a row.
				 * Adds a suffix field with a small ＋ button visible on hover.
				 */
				const attachSplitTrigger = (
					rowData: Record<string, unknown>,
					activityId: number,
					contractId: number | null,
					rowsForCombo: MonthlyAllocationGridRow[]
				) => {
					if (!canSplit) return;
					const usedIds = new Set<number>();
					for (const r of rowsForCombo) {
						if (r.accountId != null) usedIds.add(r.accountId);
					}
					if (!accounts.some((a) => !usedIds.has(a.id))) return;

					// Mark this row so the renderer can show a ＋ button
					rowData.splitTargetActivityId = activityId;
					rowData.splitTargetContractId = contractId;

					// Merge with any existing fieldsSuffix (in case row already has one)
					const existingSuffix = (rowData.fieldsSuffix as Record<string, React.ReactNode>) || {};
					rowData.fieldsSuffix = {
						...existingSuffix,
						name: (
							<>
								{existingSuffix.name}
								<Tooltip title="Split across accounts" enterDelay={300}>
									<IconButton
										size="small"
										onClick={(e: React.MouseEvent<HTMLElement>) => {
											e.stopPropagation();
											handleOpenSplitMenu(e, activityId, contractId);
										}}
										sx={{
											ml: 0.5,
											p: 0.25,
											opacity: 0.4,
											transition: 'opacity 0.15s',
											'&:hover': {
												opacity: 1,
												backgroundColor: HIERARCHY_COLORS.account + '18',
											},
											color: HIERARCHY_COLORS.account,
										}}>
										<IconAdd sx={{ fontSize: 20 }} />
									</IconButton>
								</Tooltip>
							</>
						),
					};
				};

				// ---- Activity header & activity-level data rows ----
				if (hasContracts) {
					const isExpanded = expandedActivities.has(node.activityId);
					const contractCount = new Set(contractRows.map((r) => r.contractId)).size;

					// Compute activity-level totals (contractId = null rows)
					let actHeaderTotal = 0;
					const actHeaderMonths: Record<string, number> = {};
					for (const r of activityLevelRows) {
						for (const [mk, amt] of Object.entries(r.months)) {
							actHeaderMonths[mk] = (actHeaderMonths[mk] || 0) + amt;
							actHeaderTotal += amt;
						}
					}

					// Compute full activity total (activity + all contract rows)
					let fullActivityTotal = actHeaderTotal;
					const fullActivityMonths: Record<string, number> = { ...actHeaderMonths };
					for (const r of contractRows) {
						for (const [mk, amt] of Object.entries(r.months)) {
							fullActivityMonths[mk] = (fullActivityMonths[mk] || 0) + amt;
							fullActivityTotal += amt;
						}
					}

					// Build the expand/collapse toggle — a visible chip showing contract count
					const contractOnlyTotalForLabel = fullActivityTotal - actHeaderTotal;
					const contractLabel =
						contractOnlyTotalForLabel > 0
							? `${contractCount} contract${contractCount === 1 ? '' : 's'} (+${contractOnlyTotalForLabel.toLocaleString()})`
							: `${contractCount} contract${contractCount === 1 ? '' : 's'}`;
					const toggleSuffix =
						contractCount > 0 ? (
							<Chip
								size="small"
								icon={
									isExpanded ? (
										<IconExpandMore sx={{ fontSize: '20px !important' }} />
									) : (
										<IconChevronRight sx={{ fontSize: '20px !important' }} />
									)
								}
								label={isExpanded ? 'Hide contracts' : contractLabel}
								onClick={(e: React.MouseEvent<HTMLElement>) => {
									e.stopPropagation();
									toggleContractExpansion(node.activityId);
								}}
								variant="outlined"
								sx={{
									ml: 1,
									height: 30,
									fontSize: '1.25rem',
									fontWeight: 500,
									cursor: 'pointer',
									borderColor: HIERARCHY_COLORS.contract + '66',
									color: HIERARCHY_COLORS.contract,
									'& .MuiChip-icon': {
										color: HIERARCHY_COLORS.contract,
										ml: '2px',
									},
									'&:hover': {
										borderColor: HIERARCHY_COLORS.contract,
										backgroundColor: 'rgba(217, 119, 6, 0.06)',
									},
								}}
							/>
						) : null;

					if (!isExpanded) {
						// ------ COLLAPSED: Activity row is editable ------
						// User enters total amounts here. Contracts hidden.
						const baseBP = dataRowBoxProps(actDepth, 'activity');
						const mergedBP = {
							...baseBP,
							name: {
								...baseBP.name,
								sx: {
									...baseBP.name.sx,
									...subtitleCss,
								},
							},
						};

						// Use activity-level total only (consistent with month cells).
						// Contract amounts are hidden — shown via the chip.
						const actRow: Record<string, unknown> = {
							id: `act-${node.activityId}`,
							name: node.activityName,
							total: actHeaderTotal,
							activityId: node.activityId,
							contractId: null,
							BoxProps: mergedBP,
							fieldsEditable,
							fieldsSticky,
						};
						// Always show account + contracts chips
						{
							const actAcctRows = activityLevelRows.filter((r) => r.accountId != null);
							const singleActAccount = actAcctRows.length === 1 ? actAcctRows[0] : null;
							const acctChip = singleActAccount
								? accountLabelChip(singleActAccount.accountName, singleActAccount.accountId)
								: null;
							if (acctChip || toggleSuffix) {
								actRow.fieldsSuffix = {
									name: (
										<>
											{acctChip}
											{toggleSuffix}
										</>
									),
								};
							}
						}
						for (const mk of monthKeys) {
							const amt = actHeaderMonths[mk] ?? 0;
							actRow[mk] = amt;
							compTotal += fullActivityMonths[mk] || 0;
							compMonths[mk] = (compMonths[mk] || 0) + (fullActivityMonths[mk] || 0);
						}
						result.push(actRow);

						// If editable, also allow account splits at activity level
						attachSplitTrigger(actRow, node.activityId, null, activityLevelRows);
					} else {
						// ------ EXPANDED: Activity header is summary, contracts editable ------
						const baseBP = groupRowBoxProps(actDepth, 'activity');
						const mergedBP = {
							...baseBP,
							name: {
								...baseBP.name,
								sx: {
									...baseBP.name.sx,
									...subtitleCss,
								},
							},
						};

						const actRow: Record<string, unknown> = {
							id: `act-${node.activityId}`,
							name: node.activityName,
							total: fullActivityTotal,
							activityId: node.activityId,
							contractId: null,
							BoxProps: mergedBP,
							fieldsEditable: [],
							fieldsSticky,
						};
						if (toggleSuffix) {
							actRow.fieldsSuffix = { name: toggleSuffix };
						}
						for (const mk of monthKeys) {
							const v = fullActivityMonths[mk];
							actRow[mk] = v != null && v > 0 ? v : '—';
							// Only add activity-level amounts here — contract amounts will
							// be added by the contract renderDataRows below, avoiding double-count.
							compTotal += actHeaderMonths[mk] || 0;
							compMonths[mk] = (compMonths[mk] || 0) + (actHeaderMonths[mk] || 0);
						}
						result.push(actRow);
					}
				} else if (activityLevelRows.length === 1) {
					// Single activity row, no contracts — render directly with icon
					renderDataRows(
						activityLevelRows,
						actDepth,
						'activity',
						(r) => r.activityName,
						() => dataRowBoxProps(actDepth, 'activity'),
						subtitleCss
					);
					// Attach account-split trigger to the row we just pushed
					if (result.length > 0) {
						attachSplitTrigger(result[result.length - 1], node.activityId, null, activityLevelRows);
					}
				} else if (activityLevelRows.length > 1) {
					// Activity split across multiple accounts, no contracts — group header + sub-rows
					const realAccountRows = activityLevelRows.filter((r) => r.accountId != null);
					const accountCount = realAccountRows.length;
					const actTotals: Record<string, number> = {};
					let actTotal = 0;
					for (const r of activityLevelRows) {
						for (const [mk, amt] of Object.entries(r.months)) {
							actTotals[mk] = (actTotals[mk] || 0) + amt;
							actTotal += amt;
						}
					}

					const baseBP = dataRowBoxProps(actDepth, 'activity');
					const mergedBP = {
						...baseBP,
						name: {
							...baseBP.name,
							sx: {
								...baseBP.name.sx,
								...subtitleCss,
							},
						},
					};
					const accountChip =
						accountCount > 0 ? (
							<Chip
								size="small"
								label={`${accountCount} account${accountCount === 1 ? '' : 's'}`}
								variant="outlined"
								sx={{
									ml: 1,
									height: 28,
									fontSize: '1.25rem',
									borderColor: HIERARCHY_COLORS.account + '66',
									color: HIERARCHY_COLORS.account,
								}}
							/>
						) : null;
					const grpRow: Record<string, unknown> = {
						id: `act-${node.activityId}`,
						name: node.activityName,
						total: actTotal,
						activityId: node.activityId,
						contractId: null,
						BoxProps: mergedBP,
						fieldsEditable: [],
						fieldsSticky,
					};
					if (accountChip) {
						grpRow.fieldsSuffix = { name: accountChip };
					}
					for (const mk of monthKeys) {
						grpRow[mk] = actTotals[mk] ?? 0;
					}
					result.push(grpRow);

					// Attach account-split trigger to the group header
					attachSplitTrigger(grpRow, node.activityId, null, activityLevelRows);

					// Account sub-rows beneath the activity header
					renderDataRows(
						activityLevelRows,
						actDepth + 1,
						'activity',
						(r) => r.accountName || `Account #${r.accountId}`,
						() => accountRowBoxProps(actDepth + 1)
					);
				} else {
					// No activity-level rows AND no contracts — just show the activity header (seed row)
					const baseBP = dataRowBoxProps(actDepth, 'activity');
					const mergedBP = {
						...baseBP,
						name: {
							...baseBP.name,
							sx: {
								...baseBP.name.sx,
								...subtitleCss,
							},
						},
					};
					const seedRow: Record<string, unknown> = {
						id: `act-${node.activityId}`,
						name: node.activityName,
						total: 0,
						activityId: node.activityId,
						contractId: null,
						BoxProps: mergedBP,
						fieldsEditable,
						fieldsSticky,
					};
					for (const mk of monthKeys) {
						seedRow[mk] = 0;
					}
					result.push(seedRow);
				}

				// ---- Contract sub-rows (depth 2) ----
				// Only render when contracts are expanded for this activity
				if (hasContracts && expandedActivities.has(node.activityId)) {
					// Group contract rows by contractId, then render each group
					const contractGroups = new Map<number, MonthlyAllocationGridRow[]>();
					for (const cRow of contractRows) {
						if (cRow.contractId == null) continue;
						const existing = contractGroups.get(cRow.contractId);
						if (existing) {
							existing.push(cRow);
						} else {
							contractGroups.set(cRow.contractId, [cRow]);
						}
					}

					const cDepth = actDepth + 1;
					for (const [, cGroupRows] of contractGroups) {
						const isSplit = cGroupRows.length > 1;

						if (isSplit) {
							// Contract header (group row, non-editable, shows contract name)
							const firstRow = cGroupRows[0];
							const contractTotals: Record<string, number> = {};
							let contractTotal = 0;
							for (const r of cGroupRows) {
								for (const [mk, amt] of Object.entries(r.months)) {
									contractTotals[mk] = (contractTotals[mk] || 0) + amt;
									contractTotal += amt;
								}
							}

							const realCAcctRows = cGroupRows.filter((r) => r.accountId != null);
							const cAcctCount = realCAcctRows.length;
							const cAccountChip =
								cAcctCount > 0 ? (
									<Chip
										size="small"
										label={`${cAcctCount} account${cAcctCount === 1 ? '' : 's'}`}
										variant="outlined"
										sx={{
											ml: 1,
											height: 28,
											fontSize: '1.25rem',
											borderColor: HIERARCHY_COLORS.account + '66',
											color: HIERARCHY_COLORS.account,
										}}
									/>
								) : null;
							const grpRow: Record<string, unknown> = {
								id: `contract-grp-${firstRow.activityId}-${firstRow.contractId}`,
								name: firstRow.contractName || `Contract #${firstRow.contractId}`,
								fieldsEditable: [],
								total: contractTotal,
								BoxProps: dataRowBoxProps(cDepth, 'contract'),
								fieldsSticky,
							};
							if (cAccountChip) {
								grpRow.fieldsSuffix = { name: cAccountChip };
							}
							for (const mk of monthKeys) {
								grpRow[mk] = contractTotals[mk] ?? 0;
							}
							result.push(grpRow);

							// Attach account-split trigger to the contract group header
							attachSplitTrigger(grpRow, firstRow.activityId, firstRow.contractId, cGroupRows);

							// Account sub-rows (depth 3)
							renderDataRows(
								cGroupRows,
								cDepth + 1,
								'contract',
								(r) => r.accountName || `Account #${r.accountId}`,
								() => accountRowBoxProps(cDepth + 1)
							);
						} else {
							// Single-account contract — render directly (no split indicator)
							const firstRow = cGroupRows[0];
							renderDataRows(
								cGroupRows,
								cDepth,
								'contract',
								(r) => r.contractName || `Contract #${r.contractId}`,
								() => dataRowBoxProps(cDepth, 'contract')
							);
							// Attach account-split trigger to the single contract row
							if (result.length > 0) {
								attachSplitTrigger(
									result[result.length - 1],
									firstRow.activityId,
									firstRow.contractId,
									cGroupRows
								);
							}
						}
					}
				}
			}

			// Patch component row total
			result[compRowIdx].total = compTotal;
			for (const mk of monthKeys) {
				const v = compMonths[mk];
				result[compRowIdx][mk] = v != null && v > 0 ? v : '—';
			}
		}

		// --- Grand totals row ---
		const totalsRow: Record<string, unknown> = {
			id: 'totals',
			name: 'Grand Total',
			total: data.grandTotal,
			BoxProps: totalBoxProps,
			fieldsSticky,
		};
		for (const mk of monthKeys) {
			const v = data.columnTotals[mk];
			totalsRow[mk] = v != null && v > 0 ? v : '—';
		}
		result.push(totalsRow);

		return result;
	}, [
		data,
		hierarchy,
		monthKeys,
		accounts,
		isEditable,
		expandedActivities,
		toggleContractExpansion,
		handleOpenSplitMenu,
		handleDeactivateAccountSplit,
		makeHeaderBoxProps,
		groupRowBoxProps,
		dataRowBoxProps,
		accountRowBoxProps,
		totalBoxProps,
		fieldsEditable,
		fieldsSticky,
		planCurrency,
		localPerReportingRate,
	]);

	// =========================================================================
	// Handlers
	// =========================================================================

	const handleDataChange = useCallback(
		(row: GridValidRowModel, field: string, value: number | undefined) => {
			const activityId = row.activityId as number | undefined;
			if (!activityId) return; // header, group, or totals row — ignore

			const contractId = (row.contractId as number | null) ?? null;
			const accountId = (row.accountId as number | null) ?? null;
			const month = field; // field IS the month key, e.g. '2026-03-01'
			updateCell(activityId, month, value ?? 0, contractId, accountId);
		},
		[updateCell]
	);

	/** Add an account split: create a zero-amount row and immediately save */
	const handleAddAccountSplit = useCallback(
		async (selectedAccountId: number) => {
			if (!splitTarget || monthKeys.length === 0) return;
			handleCloseSplitMenu();
			const { activityId, contractId } = splitTarget;
			const firstMonth = monthKeys[0];
			// Create a zero-amount row for the selected account
			updateCell(activityId, firstMonth, 0, contractId, selectedAccountId);
			// Force immediate save (skip debounce) so the new row appears
			await saveAll();
			// Refetch to pick up the newly-created account row from the server
			// (updateCell can only update existing rows, not add new ones)
			await refetch();
		},
		[splitTarget, monthKeys, updateCell, saveAll, handleCloseSplitMenu, refetch]
	);

	/** Get accounts already used for a given (activityId, contractId) combo */
	const getUsedAccountIds = useCallback(
		(activityId: number, contractId: number | null): Set<number> => {
			if (!data) return new Set();
			const used = new Set<number>();
			for (const row of data.rows) {
				if (
					row.activityId === activityId &&
					row.contractId === contractId &&
					row.accountId != null
				) {
					used.add(row.accountId);
				}
			}
			return used;
		},
		[data]
	);

	/** Determine available accounts for the split menu (exclude already-used) */
	const availableAccounts = useMemo(() => {
		if (!splitTarget || accounts.length === 0) return [];
		const used = getUsedAccountIds(splitTarget.activityId, splitTarget.contractId);
		return accounts.filter((a) => !used.has(a.id));
	}, [splitTarget, accounts, getUsedAccountIds]);

	/** Compute account-level summary totals from grid data */
	const accountSummary = useMemo(() => {
		if (!data) return [];
		const acctMap = new Map<
			string,
			{ accountId: number | null; accountName: string; total: number }
		>();
		for (const row of data.rows) {
			const key = row.accountId != null ? String(row.accountId) : 'unassigned';
			const existing = acctMap.get(key);
			if (existing) {
				existing.total += row.total;
			} else {
				acctMap.set(key, {
					accountId: row.accountId,
					accountName:
						row.accountName || (row.accountId ? `Account #${row.accountId}` : 'Unassigned'),
					total: row.total,
				});
			}
		}
		return Array.from(acctMap.values()).filter((a) => a.total > 0);
	}, [data]);

	// =========================================================================
	// Render
	// =========================================================================

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ mb: 2 }}>
				{error}
			</Alert>
		);
	}

	if (!data || data.rows.length === 0) {
		return (
			<Alert severity="info" sx={{ mb: 2 }}>
				No activities found for this spend plan's work plan. Link a work plan with activities first,
				then use the monthly worksheet to plan spending.
			</Alert>
		);
	}

	const autoSaveChip = saving ? (
		<Chip
			icon={<CircularProgress size={14} />}
			label="Saving…"
			size="small"
			color="primary"
			variant="outlined"
		/>
	) : isEditable ? (
		<Chip
			icon={<IconSave sx={{ fontSize: 20 }} />}
			label="Auto-saves after editing"
			size="small"
			variant="outlined"
			sx={{ color: theme.palette.brand.grey[500] }}
		/>
	) : null;

	// Currency context for the worksheet. Cells (and the Grand Total) are
	// denominated in the plan's account currency; activities may be budgeted in
	// the reporting currency. Surface both the worksheet currency and the rate
	// in play so the reader doesn't have to infer the bridge between them.
	const showFxBridge =
		!!planCurrency &&
		!!reportingCurrency &&
		planCurrency !== reportingCurrency &&
		localPerReportingRate != null &&
		localPerReportingRate > 0;
	const worksheetTotalReporting = showFxBridge
		? data.grandTotal / (localPerReportingRate as number)
		: null;

	return (
		<Stack spacing={2}>
			{/* Currency context: which currency the cells are in + the rate used */}
			{planCurrency && (
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
					<Chip
						size="small"
						color="info"
						variant="outlined"
						label={`Amounts in ${planCurrency}`}
					/>
					{showFxBridge && (
						<Tooltip
							title={`Fixed budget rate per the work plan (its local-currency budget ÷ its ${reportingCurrency} budget). Not a live market rate — it's the basis used to restate ${reportingCurrency} activity budgets into ${planCurrency}.`}>
							<Typography
								variant="caption"
								sx={{
									color: theme.palette.brand.grey[500],
									cursor: 'help',
									borderBottom: `1px dotted ${theme.palette.brand.grey[400]}`,
								}}>
								1 {reportingCurrency} ={' '}
								{formatCurrency({
									value: localPerReportingRate as number,
									currency: planCurrency,
								})}
							</Typography>
						</Tooltip>
					)}
				</Stack>
			)}

			{/* Toolbar row: left (custom content) */}
			{toolbarLeft && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						minHeight: 32,
					}}>
					<Box>{toolbarLeft}</Box>
				</Box>
			)}

			{/* Hierarchical Grid */}
			<TableSheet
				columns={columns}
				defaultExpanded={true}
				headerAction={autoSaveChip}
				isExpandable={false}
				label="Monthly Spend Worksheet"
				onDataChange={handleDataChange}
				palette="blue"
				rows={rows}
			/>

			{/* Worksheet total restated in the reporting currency */}
			{worksheetTotalReporting != null && (
				<Typography
					variant="caption"
					sx={{ alignSelf: 'flex-end', color: theme.palette.brand.grey[500] }}>
					Worksheet total {formatCurrency({ value: data.grandTotal, currency: planCurrency })} ≈{' '}
					{formatCurrency({ value: worksheetTotalReporting, currency: reportingCurrency })}
				</Typography>
			)}

			{/* Account picker menu for "＋ Add account" rows */}
			<Menu
				anchorEl={splitMenuAnchor}
				open={Boolean(splitMenuAnchor)}
				onClose={handleCloseSplitMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						sx: { minWidth: 240, maxHeight: 320 },
					},
				}}>
				<MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 600, fontSize: '1.25rem' }}>
					Select account to add
				</MenuItem>
				{availableAccounts.length === 0 && (
					<MenuItem disabled>
						<em>No more accounts available</em>
					</MenuItem>
				)}
				{availableAccounts.map((acct) => (
					<MenuItem
						key={acct.id}
						onClick={() => handleAddAccountSplit(acct.id)}
						sx={{ fontSize: '1.25rem' }}>
						<Stack>
							<Box>{acct.name || acct.num || `Account #${acct.id}`}</Box>
							{acct.currency && (
								<Box sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
									{acct.type} · {acct.currency}
								</Box>
							)}
						</Stack>
					</MenuItem>
				))}
			</Menu>

			{/* Summary by account — shown when there are account splits */}
			{accountSummary.length > 1 && (
				<Box
					sx={{
						mt: 2,
						p: 2,
						backgroundColor: theme.palette.brand.grey[100],
						borderRadius: 1,
						border: `1px solid ${theme.palette.brand.grey[200]}`,
					}}>
					<Box
						sx={{
							fontWeight: 600,
							fontSize: '1.25rem',
							color: theme.palette.brand.grey[700],
							mb: 1,
						}}>
						Summary by Account
					</Box>
					<Stack spacing={0.5}>
						{accountSummary.map((a) => (
							<Box
								key={a.accountId ?? 'unassigned'}
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									fontSize: '1.25rem',
									color: theme.palette.brand.grey[600],
									py: 0.25,
								}}>
								<Box>{a.accountName}</Box>
								<Box sx={{ fontWeight: 600 }}>{a.total.toLocaleString()}</Box>
							</Box>
						))}
					</Stack>
				</Box>
			)}
		</Stack>
	);
};
